import { useState, useEffect, useCallback } from 'react';
import { getFlashDealProducts } from '@/mocks/products';
import ProductCard from '@/components/feature/ProductCard';

function CountdownTimer({ endDate }: { endDate: string }) {
  const [timeLeft, setTimeLeft] = useState({ h: 0, m: 0, s: 0 });

  const calculateTime = useCallback(() => {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return { h: 0, m: 0, s: 0 };
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const s = Math.floor((diff % (1000 * 60)) / 1000);
    return { h, m, s };
  }, [endDate]);

  useEffect(() => {
    setTimeLeft(calculateTime());
    const timer = setInterval(() => setTimeLeft(calculateTime()), 1000);
    return () => clearInterval(timer);
  }, [calculateTime]);

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm text-white/90 mr-1 hidden sm:inline">Time Left:</span>
      <span className="text-sm font-bold text-white">
        {String(timeLeft.h).padStart(2, '0')}h : {String(timeLeft.m).padStart(2, '0')}m : {String(timeLeft.s).padStart(2, '0')}s
      </span>
    </div>
  );
}

export default function FlashDeals() {
  const flashProducts = getFlashDealProducts().slice(0, 6);
  const firstDeal = flashProducts[0];

  return (
    <section className="w-full py-4 md:py-6 px-4 md:px-6 lg:px-8">
      <div className="w-full bg-white rounded-lg overflow-hidden">
        {/* Red header bar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-700">
          <div className="flex items-center gap-2">
            <i className="ri-flashlight-fill text-xl text-yellow-300" />
            <span className="text-white font-bold text-base md:text-lg">
              Flash Sales | Live Now
            </span>
          </div>
          {firstDeal?.flashDealEnds && (
            <CountdownTimer endDate={firstDeal.flashDealEnds} />
          )}
          <a
            href="/flash-deals"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-white hover:text-white/80 transition-colors whitespace-nowrap"
          >
            See All
            <i className="ri-arrow-right-s-line" />
          </a>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-0">
          {flashProducts.map((product) => (
            <div key={product.id} className="border-r border-b border-gray-100 last:border-r-0">
              <ProductCard product={product} variant="compact" showProgressBar />
            </div>
          ))}
        </div>

        <a
          href="/flash-deals"
          className="sm:hidden flex items-center justify-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors py-3 border-t border-gray-100"
        >
          See All Flash Deals
          <i className="ri-arrow-right-s-line" />
        </a>
      </div>
    </section>
  );
}